import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Alert, AlertTitle, Box, Paper, Skeleton, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { CoverageSlotSelector } from './components/merge-customization/CoverageSlotSelector';
import { DemographicFieldSelector } from './components/merge-customization/DemographicFieldSelector';
import { MergeCustomizationHeader } from './components/merge-customization/MergeCustomizationHeader';
import { MergePreviewPanel } from './components/merge-customization/MergePreviewPanel';
import { ModuleSection } from './components/merge-customization/ModuleSection';
import { useDuplicateDetection } from './hooks/useDuplicateDetection';
import { useMergeCustomization } from './hooks/useMergeCustomization';
import type { MergeCustomizationLocationState } from './types/merge-customization';

export const MergeCustomizationPage: React.FC = () => {
  const location = useLocation();
  const state = location.state as MergeCustomizationLocationState | null;

  // No longer requires primaryPatientId — only group + organizationId
  if (!state?.group || !state?.organizationId) {
    return <Navigate to="/patients/merge" replace />;
  }

  return <MergeCustomizationContent state={state} />;
};

/** Inner component so the hook is only called when state is valid. */
function MergeCustomizationContent({ state }: { state: MergeCustomizationLocationState }) {
  const {
    modules,
    selections,
    toggleItem,
    selectAllFromPatient,
    selectedCount,
    isLoading,
    isCoverageLoading,
    isMerging,
    fetchErrors,
    confirmMerge,
    patientColors,
    defaultPatientId,
    activePatients,
    demographicSelections,
    setDemographicFieldSource,
    coverageSelections,
    setCoverageSelections,
  } = useMergeCustomization(state);

  const { isDuplicate } = useDuplicateDetection(modules);

  // Fire once when data finishes loading
  const { patientDeduplication } = useEventTracking();
  const trackedRef = useRef(false);
  useEffect(() => {
    if (isLoading || trackedRef.current) return;
    trackedRef.current = true;
    patientDeduplication.mergeCustomizationViewed({
      organization_id: state.organizationId,
      group_id: state.group.group_id,
      patient_count: activePatients.length,
      module_count: Object.keys(modules).length,
    });
  }, [isLoading, activePatients.length, modules, state, patientDeduplication]);

  // Track which left-panel accordion is expanded
  const [activeSection, setActiveSection] = useState<string | null>('demographics');

  const handleSectionToggle = useCallback((section: string) => {
    setActiveSection(prev => (prev === section ? null : section));
  }, []);

  // Build per-module patient lists for select-all buttons
  const modulePatients = useMemo(() => {
    const result: Record<string, { id: string; name: string }[]> = {};
    for (const [moduleName, mod] of Object.entries(modules)) {
      const seen = new Set<string>();
      const patients: { id: string; name: string }[] = [];
      for (const item of mod.items) {
        if (seen.has(item.patientId)) continue;
        seen.add(item.patientId);
        patients.push({ id: item.patientId, name: item.patientName });
      }
      result[moduleName] = patients;
    }
    return result;
  }, [modules]);

  const errorPatientIds = Object.keys(fetchErrors);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <MergeCustomizationHeader
        selectedCount={selectedCount}
        isMerging={isMerging}
        onConfirm={confirmMerge}
      />

      {/* Info banner */}
      <Alert severity="info" sx={{ mb: 2 }}>
        Review the information from each patient and choose which items to keep.
        <br />
        Everything you select will carry over into the merged record.
        Appointments, forms, documents, labs, and notes from all patients will automatically be merged into a single record.
      </Alert>

      {/* Fetch errors */}
      {errorPatientIds.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Some patient data could not be loaded</AlertTitle>
          {errorPatientIds.map(id => (
            <Typography key={id} variant="body2">
              Patient {id.substring(0, 8)}...: {fetchErrors[id]}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Main content */}
      {isLoading ? (
        <Paper sx={{ p: 3 }}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} variant="rectangular" sx={{height: 60,  mb: 1, borderRadius: 1 }} />
          ))}
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          {/* Left panel */}
          <Box sx={{ flex: 2, minWidth: 0 }}>
            {/* Demographics — first accordion, expanded by default */}
            <DemographicFieldSelector
              patients={activePatients}
              defaultPatientId={defaultPatientId}
              selections={demographicSelections}
              onSelectionChange={setDemographicFieldSource}
              patientColors={patientColors}
              expanded={activeSection === 'demographics'}
              onToggle={() => handleSectionToggle('demographics')}
            />

            {/* Coverages — second accordion, drag-and-drop between slots */}
            <CoverageSlotSelector
              patients={activePatients}
              onSelectionsChange={setCoverageSelections}
              patientColors={patientColors}
              expanded={activeSection === 'coverages'}
              onToggle={() => handleSectionToggle('coverages')}
              isLoading={isCoverageLoading}
            />

            {Object.keys(modules).length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No module data found for the patients in this group.
                </Typography>
              </Paper>
            ) : (
              Object.entries(modules).map(([moduleName, mod]) => (
                <ModuleSection
                  key={moduleName}
                  module={mod}
                  moduleName={moduleName}
                  selections={selections}
                  onToggleItem={toggleItem}
                  onSelectAllFromPatient={selectAllFromPatient}
                  isDuplicate={isDuplicate}
                  patientColors={patientColors}
                  patients={modulePatients[moduleName] ?? []}
                  expanded={activeSection === `module:${moduleName}`}
                  onToggle={() => handleSectionToggle(`module:${moduleName}`)}
                />
              ))
            )}
          </Box>

          {/* Right panel — preview */}
          <Box sx={{ flex: 1, minWidth: 280, display: { xs: 'none', md: 'block' } }}>
            <MergePreviewPanel
              modules={modules}
              selections={selections}
              patients={activePatients}
              defaultPatientId={defaultPatientId}
              demographicSelections={demographicSelections}
              coverageSelections={coverageSelections}
              activeSection={activeSection}
              onSectionChange={handleSectionToggle}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default MergeCustomizationPage;
